import { type ResolutionPayload } from '#app/services/ai/types.ts'
import { type LegalSourceCitationBehavior } from '#app/services/legal-ai/shared/source-contract.ts'
import { normalizeResolutionLikeCitationLeadIn } from '#app/services/legal-ai/shared/resolution-citation-lead-in.ts'
import { capitalizeAllWords } from '#app/utils/text.js'

type ResolutionCitationPayload = Omit<ResolutionPayload, 'content'> & { id: string | number }

export function getNexusUrl(documentId: string) {
	return 'https://nexuspj.poder-judicial.go.cr/document/' + documentId
}

export const resolutionNumberAsTitle = (resolutionPayload: ResolutionCitationPayload) => {
	if (resolutionPayload.tipoDocumento === 'ACT') {
		return `${resolutionPayload.tipoInformacion} - ${resolutionPayload.date}`
	}
	let title = resolutionPayload.title
	let tribunalName = resolutionPayload.nombre_tribunal
	let year = resolutionPayload.year
	let number = resolutionPayload.numeroDocumento
	if (!number || !tribunalName || !year) {
		return title
	}
	let fullCapitalizedTribunalName = capitalizeAllWords(tribunalName)

	return `Resolución ${number}-${year} del ${fullCapitalizedTribunalName}`
}

function getCanonicalResolutionNumber(payload: ResolutionCitationPayload) {
	const number = String(payload.numeroDocumento ?? '').trim()
	const year = String(payload.year ?? '').trim()
	if (!number || !year) return null
	return new RegExp(`(?:-|/)${year}$`).test(number) ? number.replace('/', '-') : `${number}-${year}`
}

export function normalizeResolutionCitationLeadIn(leadIn: string, payload: ResolutionCitationPayload) {
	const canonicalNumber = getCanonicalResolutionNumber(payload)
	if (!canonicalNumber) return leadIn
	return normalizeResolutionLikeCitationLeadIn({
		leadIn,
		canonicalNumber,
		labelPattern: 'resoluci[oó]n|voto',
	})
}

export const costaRicaResolucionesCitation: LegalSourceCitationBehavior<ResolutionPayload> = {
	title: resolutionNumberAsTitle,
	url: payload => getNexusUrl(payload.idDocument),
	inlineTagName: 'resolutiondocument',
	modelAlias: { prefix: 'R' },
	normalizeCitationLeadIn: normalizeResolutionCitationLeadIn,
	legacyTagNames: ['ResolutionDocument', 'resolution-document'],
	fallbackUrl: getNexusUrl,
	iconName: 'maze',
	linkClassName: 'text-indigo-600',
	summaryLabel: 'resoluciones',
	summaryIconName: 'gavel',
	summaryIconClassName: 'text-blue-500',
	summaryDotClassName: 'bg-blue-500',
	summaryTitle: payload => payload.title,
}
